import { format } from 'date-fns' // eslint-disable-line
import { ptBR } from 'date-fns/locale' // eslint-disable-line
import jsPDF from 'jspdf'

import {
  FechamentoData,
  PaymentType,
  PAYMENT_TYPE_LABELS,
  PAYMENT_TYPE_ORDER,
} from 'src/service/fechamento.service'

const BRAND: [number, number, number] = [102, 126, 234]
const INK: [number, number, number] = [33, 43, 54]
const MUTED: [number, number, number] = [99, 115, 129]
const RED: [number, number, number] = [200, 50, 50]
const GREEN: [number, number, number] = [34, 139, 34]
const ZEBRA: [number, number, number] = [245, 247, 250]

const MARGIN = 14
const PAGE_BOTTOM = 280

const fmtCurrency = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format((v || 0) / 100)

const fmtDateTime = (value?: string | Date | null) => {
  if (!value) return '—'
  return format(new Date(value), 'dd/MM/yy HH:mm', { locale: ptBR })
}

function loadLogoAsDataUrl(): Promise<string | null> {
  return new Promise((resolve) => {
    // eslint-disable-next-line
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.naturalWidth
      canvas.height = img.naturalHeight
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        resolve(null)
        return
      }
      ctx.drawImage(img, 0, 0)
      resolve(canvas.toDataURL('image/png'))
    }
    img.onerror = () => resolve(null)
    img.src = '/logo/logo.svg'
  })
}

type Align = 'left' | 'right' | 'center'

type Column = {
  header: string
  width: number // fração (0-1) da largura útil
  align?: Align
}

type Cell = {
  text: string
  color?: [number, number, number]
  bold?: boolean
  align?: Align
}

type TableOptions = {
  columns: Column[]
  rows: Cell[][]
  startY: number
  fontSize?: number
  totalsRow?: Cell[]
}

export interface FechamentoPdfInput {
  eventName: string
  data: FechamentoData
  startDate: Date | null
  endDate: Date | null
}

export async function generateFechamentoPdf(input: FechamentoPdfInput) {
  const { eventName, data } = input
  const logo = await loadLogoAsDataUrl()

  // eslint-disable-next-line new-cap
  const doc = new jsPDF()
  const pageWidth = doc.internal.pageSize.getWidth()
  const contentWidth = pageWidth - MARGIN * 2

  // -------------------------------------------------------------------------
  // Helpers de desenho
  // -------------------------------------------------------------------------
  const drawHeaderBand = () => {
    if (logo) {
      const logoH = 9
      const logoW = logoH * (3652 / 735)
      doc.addImage(logo, 'PNG', MARGIN, 12, logoW, logoH)
    }
    doc.setTextColor(...MUTED)
    doc.setFontSize(8)
    doc.setFont('helvetica', 'normal')
    doc.text(
      `Emitido em ${format(new Date(), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}`,
      pageWidth - MARGIN,
      17,
      { align: 'right' },
    )
    doc.setDrawColor(...BRAND)
    doc.setLineWidth(0.6)
    doc.line(MARGIN, 25, pageWidth - MARGIN, 25)
  }

  const drawFooter = (pageNum: number, pageTotal: number) => {
    const y = 288
    doc.setDrawColor(225, 228, 232)
    doc.setLineWidth(0.3)
    doc.line(MARGIN, y, pageWidth - MARGIN, y)
    doc.setTextColor(...MUTED)
    doc.setFontSize(7)
    doc.setFont('helvetica', 'normal')
    doc.text('DevBrasil Events · Resumão do Evento', MARGIN, y + 5)
    doc.text(`Página ${pageNum} de ${pageTotal}`, pageWidth - MARGIN, y + 5, { align: 'right' })
  }

  const sectionTitle = (title: string, y: number): number => {
    doc.setTextColor(...INK)
    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    doc.text(title, MARGIN, y)
    doc.setDrawColor(...BRAND)
    doc.setLineWidth(0.7)
    doc.line(MARGIN, y + 2, MARGIN + 28, y + 2)
    return y + 9
  }

  let pageCount = 1
  const newPage = () => {
    doc.addPage()
    pageCount += 1
    drawHeaderBand()
    return 33
  }

  const ensure = (y: number, needed: number): number => (y + needed > PAGE_BOTTOM ? newPage() : y)

  // Tabela genérica com cabeçalho, zebra, quebra de página e linha de totais.
  const drawTable = (opts: TableOptions): number => {
    const { columns, rows, totalsRow } = opts
    const fontSize = opts.fontSize ?? 8.5
    const rowH = fontSize < 8 ? 6 : 7
    let y = opts.startY

    const xs: number[] = []
    let acc = MARGIN
    columns.forEach((col) => {
      xs.push(acc)
      acc += col.width * contentWidth
    })

    const cellX = (idx: number, align: Align) => {
      if (align === 'right') return xs[idx] + columns[idx].width * contentWidth - 2
      if (align === 'center') return xs[idx] + (columns[idx].width * contentWidth) / 2
      return xs[idx] + 2
    }

    const drawHead = () => {
      doc.setFillColor(...BRAND)
      doc.rect(MARGIN, y, contentWidth, rowH, 'F')
      doc.setTextColor(255, 255, 255)
      doc.setFontSize(fontSize)
      doc.setFont('helvetica', 'bold')
      columns.forEach((col, idx) => {
        const align = col.align ?? 'left'
        doc.text(col.header, cellX(idx, align), y + rowH - 2, { align })
      })
      y += rowH
    }

    drawHead()

    rows.forEach((row, rIdx) => {
      if (y + rowH > PAGE_BOTTOM) {
        y = newPage()
        drawHead()
      }
      if (rIdx % 2 === 1) {
        doc.setFillColor(...ZEBRA)
        doc.rect(MARGIN, y, contentWidth, rowH, 'F')
      }
      doc.setFontSize(fontSize)
      columns.forEach((col, idx) => {
        const cell = row[idx] || { text: '' }
        const align = col.align ?? 'left'
        doc.setTextColor(...(cell.color ?? INK))
        doc.setFont('helvetica', cell.bold ? 'bold' : 'normal')
        doc.text(cell.text, cellX(idx, align), y + rowH - 2, { align })
      })
      y += rowH
    })

    if (totalsRow) {
      if (y + rowH > PAGE_BOTTOM) {
        y = newPage()
        drawHead()
      }
      doc.setDrawColor(...INK)
      doc.setLineWidth(0.3)
      doc.line(MARGIN, y, MARGIN + contentWidth, y)
      doc.setFillColor(238, 241, 245)
      doc.rect(MARGIN, y, contentWidth, rowH, 'F')
      doc.setFontSize(fontSize)
      columns.forEach((col, idx) => {
        const cell = totalsRow[idx] || { text: '' }
        const align = col.align ?? 'left'
        doc.setTextColor(...(cell.color ?? INK))
        doc.setFont('helvetica', 'bold')
        doc.text(cell.text, cellX(idx, align), y + rowH - 2, { align })
      })
      y += rowH
    }

    return y
  }

  // -------------------------------------------------------------------------
  // Conteúdo
  // -------------------------------------------------------------------------
  drawHeaderBand()
  let y = 33

  // Título
  doc.setTextColor(...INK)
  doc.setFontSize(17)
  doc.setFont('helvetica', 'bold')
  doc.text('Resumão do Evento', MARGIN, y)
  y += 7
  doc.setTextColor(...BRAND)
  doc.setFontSize(13)
  doc.text(eventName, MARGIN, y)
  y += 8

  // Identificação do evento
  const ev = data.event
  const period = [
    data.period.start ? `De ${format(new Date(data.period.start), 'dd/MM/yyyy HH:mm')}` : '',
    data.period.end ? `Até ${format(new Date(data.period.end), 'dd/MM/yyyy HH:mm')}` : '',
  ]
    .filter(Boolean)
    .join('  ·  ')

  const infoLines = [
    ev?.date ? `Data do evento: ${format(new Date(ev.date), 'dd/MM/yyyy')}` : '',
    ev?.local ? `Local: ${ev.local}` : '',
    ev?.document ? `Documento: ${ev.document}` : '',
    period ? `Período apurado: ${period}` : 'Período apurado: Todo o evento',
  ].filter(Boolean)

  doc.setTextColor(...MUTED)
  doc.setFontSize(9)
  doc.setFont('helvetica', 'normal')
  infoLines.forEach((line) => {
    doc.text(line, MARGIN, y)
    y += 5
  })
  y += 4

  // ---- DRE Resumo ----
  y = sectionTitle('Resumo do Resultado', y)
  const t = data.totals
  const taxLines: Cell[][] = PAYMENT_TYPE_ORDER.filter(
    (type) => data.paymentTotals[type].tax > 0,
  ).map((type) => {
    const slot = data.paymentTotals[type]
    const rate = slot.gross > 0 ? (slot.tax / slot.gross) * 100 : 0
    return [
      {
        text: `   (-) Taxa ${PAYMENT_TYPE_LABELS[type]} (média ${rate
          .toFixed(2)
          .replace('.', ',')}%)`,
        color: MUTED,
      },
      { text: `- ${fmtCurrency(slot.tax)}`, align: 'right', color: RED } as Cell,
    ]
  })
  const dreRows: Cell[][] = [
    [
      { text: 'Receita Bruta (total vendido)', bold: true },
      { text: fmtCurrency(t.gross), align: 'right', bold: true } as Cell,
    ],
    ...taxLines,
    [
      { text: '(=) Total de Taxas', bold: true },
      { text: `- ${fmtCurrency(t.tax)}`, align: 'right', bold: true, color: RED } as Cell,
    ],
    [
      { text: '(=) Receita Líquida (após taxas)', bold: true },
      { text: fmtCurrency(t.net), align: 'right', bold: true, color: GREEN } as Cell,
    ],
  ]
  y = drawTable({
    startY: y,
    fontSize: 10,
    columns: [
      { header: 'Descrição', width: 0.6 },
      { header: 'Valor', width: 0.4, align: 'right' },
    ],
    rows: dreRows,
  })
  y += 3
  doc.setTextColor(...MUTED)
  doc.setFontSize(8.5)
  doc.setFont('helvetica', 'normal')
  doc.text(
    `Pedidos: ${t.orderCount}   ·   Vendedores: ${t.sellerCount}   ·   Ticket médio: ${fmtCurrency(
      t.averageTicket,
    )}`,
    MARGIN,
    y,
  )
  y += 10

  // ---- Receita por forma de pagamento ----
  y = ensure(y, 40)
  y = sectionTitle('Receita por Forma de Pagamento', y)
  const payRows: Cell[][] = PAYMENT_TYPE_ORDER.map((type) => {
    const slot = data.paymentTotals[type]
    const pct = t.gross > 0 ? ((slot.gross / t.gross) * 100).toFixed(1) : '0.0'
    return [
      { text: PAYMENT_TYPE_LABELS[type], bold: true },
      { text: String(slot.count), align: 'right' },
      { text: fmtCurrency(slot.gross), align: 'right' },
      { text: `- ${fmtCurrency(slot.tax)}`, align: 'right', color: RED },
      { text: fmtCurrency(slot.net), align: 'right', color: GREEN, bold: true },
      { text: `${pct}%`, align: 'right', color: MUTED },
    ] as Cell[]
  })
  y = drawTable({
    startY: y,
    columns: [
      { header: 'Forma de Pagamento', width: 0.28 },
      { header: 'Nº Vendas', width: 0.12, align: 'right' },
      { header: 'Bruto', width: 0.2, align: 'right' },
      { header: 'Taxa', width: 0.16, align: 'right' },
      { header: 'Líquido', width: 0.16, align: 'right' },
      { header: '%', width: 0.08, align: 'right' },
    ],
    rows: payRows,
    totalsRow: [
      { text: 'TOTAL' },
      { text: String(t.orderCount), align: 'right' },
      { text: fmtCurrency(t.gross), align: 'right' },
      { text: `- ${fmtCurrency(t.tax)}`, align: 'right', color: RED },
      { text: fmtCurrency(t.net), align: 'right', color: GREEN },
      { text: '100%', align: 'right' },
    ],
  })
  y += 10

  // ---- Resumo por vendedor ----
  y = ensure(y, 40)
  y = sectionTitle('Resumo por Vendedor', y)
  const sellerRows: Cell[][] = data.sellers.map((s) => [
    { text: s.sellerName, bold: true },
    { text: String(s.orderCount), align: 'right' },
    { text: fmtCurrency(s.gross), align: 'right' },
    { text: `- ${fmtCurrency(s.tax)}`, align: 'right', color: RED },
    { text: fmtCurrency(s.net), align: 'right', color: GREEN, bold: true },
    { text: fmtDateTime(s.firstSale), align: 'right', color: MUTED },
    { text: fmtDateTime(s.lastSale), align: 'right', color: MUTED },
  ])
  y = drawTable({
    startY: y,
    fontSize: 8,
    columns: [
      { header: 'Vendedor', width: 0.26 },
      { header: 'Ped.', width: 0.08, align: 'right' },
      { header: 'Bruto', width: 0.15, align: 'right' },
      { header: 'Taxa', width: 0.13, align: 'right' },
      { header: 'Líquido', width: 0.14, align: 'right' },
      { header: '1ª venda', width: 0.12, align: 'right' },
      { header: 'Última', width: 0.12, align: 'right' },
    ],
    rows: sellerRows,
    totalsRow: [
      { text: 'TOTAL' },
      { text: String(t.orderCount), align: 'right' },
      { text: fmtCurrency(t.gross), align: 'right' },
      { text: `- ${fmtCurrency(t.tax)}`, align: 'right', color: RED },
      { text: fmtCurrency(t.net), align: 'right', color: GREEN },
      { text: '', align: 'right' },
      { text: '', align: 'right' },
    ],
  })
  y += 10

  // ---- Vendedor x Forma de pagamento (matriz de valores brutos) ----
  y = ensure(y, 40)
  y = sectionTitle('Vendas por Vendedor e Forma de Pagamento', y)

  const matrixCols: Column[] = [
    { header: 'Vendedor', width: 0.28 },
    ...PAYMENT_TYPE_ORDER.map((type) => ({
      header: PAYMENT_TYPE_LABELS[type],
      width: 0.144,
      align: 'right' as Align,
    })),
  ]
  const matrixRows: Cell[][] = data.sellers.map((s) => [
    { text: s.sellerName, bold: true },
    ...PAYMENT_TYPE_ORDER.map(
      (type: PaymentType) => ({ text: fmtCurrency(s.payments[type].gross), align: 'right' } as Cell),
    ),
  ])
  const matrixTotals: Cell[] = [
    { text: 'TOTAL' },
    ...PAYMENT_TYPE_ORDER.map(
      (type: PaymentType) =>
        ({ text: fmtCurrency(data.paymentTotals[type].gross), align: 'right' } as Cell),
    ),
  ]
  y = drawTable({
    startY: y,
    fontSize: 7.5,
    columns: matrixCols,
    rows: matrixRows,
    totalsRow: matrixTotals,
  })
  y += 10

  // ---- Produto x Forma de pagamento (matriz de valores brutos) ----
  y = ensure(y, 40)
  y = sectionTitle('Vendas por Produto e Forma de Pagamento', y)

  const prodCols: Column[] = [
    { header: 'Produto', width: 0.24 },
    { header: 'Unid.', width: 0.08, align: 'right' as Align },
    ...PAYMENT_TYPE_ORDER.map((type) => ({
      header: PAYMENT_TYPE_LABELS[type],
      width: 0.1,
      align: 'right' as Align,
    })),
    { header: 'Total', width: 0.18, align: 'right' as Align },
  ]
  const prodRows: Cell[][] = data.products.map((p) => [
    { text: p.productName, bold: true },
    { text: String(p.totalQuantity), align: 'right' } as Cell,
    ...PAYMENT_TYPE_ORDER.map(
      (type: PaymentType) => ({ text: fmtCurrency(p.payments[type].gross), align: 'right' } as Cell),
    ),
    { text: fmtCurrency(p.totalRevenue), align: 'right', bold: true } as Cell,
  ])
  const prodTotals: Cell[] = [
    { text: 'TOTAL' },
    {
      text: String(data.products.reduce((acc, p) => acc + p.totalQuantity, 0)),
      align: 'right',
    } as Cell,
    ...PAYMENT_TYPE_ORDER.map(
      (type: PaymentType) =>
        ({
          text: fmtCurrency(data.products.reduce((acc, p) => acc + p.payments[type].gross, 0)),
          align: 'right',
        } as Cell),
    ),
    {
      text: fmtCurrency(data.products.reduce((acc, p) => acc + p.totalRevenue, 0)),
      align: 'right',
    } as Cell,
  ]
  y = drawTable({
    startY: y,
    fontSize: 7.5,
    columns: prodCols,
    rows: prodRows,
    totalsRow: prodTotals,
  })

  // -------------------------------------------------------------------------
  // Rodapé em todas as páginas
  // -------------------------------------------------------------------------
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i)
    drawFooter(i, pageCount)
  }

  const slug = eventName.replace(/\s+/g, '-').toLowerCase()
  doc.save(`resumao-${slug}-${format(new Date(), 'yyyy-MM-dd')}.pdf`)
}
